// Portal trial math — a faithful TypeScript port of the game's resolution engine
// (aitech.tapper-backend). The game is the source of truth; this module mirrors it
// line-for-line so the editor's manual-play mode (Module 3) and symbolic verifier
// (Module 2) compute exactly what a real avatar would experience in the game.
//
// Ported from:
//   Tapper.Buisiness/Services/Avatars/AbilityCalculator.cs   — stat derivation
//   Tapper.Buisiness/Services/Portals/AdventureService.cs    — success/degree formula
//
// C# `(int)` casts are truncation-toward-zero; all values here are non-negative so
// `Math.trunc` reproduces them exactly. A golden-vector test pins every constant to
// the C# so a change on the game side surfaces as a loud diff. No side effects.
import { AvatarAbility, AvatarRarity } from './contracts';
// ── Game-side enums (mirror the C# ordinals) ────────────────────────────────
/** Ability axes as the GAME orders them (AbilityType.cs). Note this differs from
 *  the editor's AvatarAbility ordering — bridge via ABILITY_EDITOR_TO_GAME. */
export var GameAbilityType;
(function (GameAbilityType) {
    GameAbilityType[GameAbilityType["Strength"] = 0] = "Strength";
    GameAbilityType[GameAbilityType["PerceptionOrPsiEnergy"] = 1] = "PerceptionOrPsiEnergy";
    GameAbilityType[GameAbilityType["Agility"] = 2] = "Agility";
    GameAbilityType[GameAbilityType["Defense"] = 3] = "Defense";
    GameAbilityType[GameAbilityType["Intellect"] = 4] = "Intellect";
})(GameAbilityType || (GameAbilityType = {}));
/** Avatar classes (AvatarClass.cs, 0..15). The editor has no equivalent yet. */
export var GameAvatarClass;
(function (GameAvatarClass) {
    GameAvatarClass[GameAvatarClass["Polymorph"] = 0] = "Polymorph";
    GameAvatarClass[GameAvatarClass["Engineer"] = 1] = "Engineer";
    GameAvatarClass[GameAvatarClass["Ranger"] = 2] = "Ranger";
    GameAvatarClass[GameAvatarClass["Titan"] = 3] = "Titan";
    GameAvatarClass[GameAvatarClass["Watcher"] = 4] = "Watcher";
    GameAvatarClass[GameAvatarClass["Paladin"] = 5] = "Paladin";
    GameAvatarClass[GameAvatarClass["Illusionist"] = 6] = "Illusionist";
    GameAvatarClass[GameAvatarClass["Monk"] = 7] = "Monk";
    GameAvatarClass[GameAvatarClass["Scout"] = 8] = "Scout";
    GameAvatarClass[GameAvatarClass["Rogue"] = 9] = "Rogue";
    GameAvatarClass[GameAvatarClass["Strategist"] = 10] = "Strategist";
    GameAvatarClass[GameAvatarClass["Mentalist"] = 11] = "Mentalist";
    GameAvatarClass[GameAvatarClass["Juggernaut"] = 12] = "Juggernaut";
    GameAvatarClass[GameAvatarClass["Hunter"] = 13] = "Hunter";
    GameAvatarClass[GameAvatarClass["Scientist"] = 14] = "Scientist";
    GameAvatarClass[GameAvatarClass["Guard"] = 15] = "Guard";
})(GameAvatarClass || (GameAvatarClass = {}));
/** Per-class-per-ability weight tier (AvatarStatType.cs). */
export var StatTier;
(function (StatTier) {
    StatTier["Main"] = "Main";
    StatTier["Advanced"] = "Advanced";
    StatTier["Universal"] = "Universal";
    StatTier["Weak"] = "Weak";
})(StatTier || (StatTier = {}));
// ── Derivation constants (AbilityCalculator.cs) ─────────────────────────────
/** GetStatMultiplier — AbilityCalculator.cs:239-254. */
export const STAT_MULTIPLIER = {
    [StatTier.Main]: 9,
    [StatTier.Advanced]: 6.5,
    [StatTier.Universal]: 5,
    [StatTier.Weak]: 4,
};
/** GetPowerMultiplier(rarity) — AbilityCalculator.cs:220-237. AvatarRarity shares
 *  the game's Rarity ordering (Common..Mythic), so it keys this directly. */
export const RARITY_MULTIPLIER = {
    [AvatarRarity.Common]: 1,
    [AvatarRarity.Rare]: 1.5,
    [AvatarRarity.Epic]: 2.5,
    [AvatarRarity.Legendary]: 5,
    [AvatarRarity.Mythic]: 12,
};
/** GetLevelMultiplier — AbilityCalculator.cs:256. */
export function levelMultiplier(level) {
    return (level + 3) / 4;
}
// Class → per-ability stat tier, transcribed verbatim from the five AbilityCalculator
// switches (GetStength/GetPerceptionOrPsiEnergy/GetAgility/GetDefense/GetIntellect).
// Each code is 5 tiers in GAME ability order [Strength, Perception, Agility, Defense,
// Intellect]; M=Main, A=Advanced, U=Universal, W=Weak.
const CLASS_TIER_CODES = {
    [GameAvatarClass.Polymorph]: 'UUUUU',
    [GameAvatarClass.Engineer]: 'AWWWA',
    [GameAvatarClass.Ranger]: 'AWAWW',
    [GameAvatarClass.Titan]: 'AWWAW',
    [GameAvatarClass.Watcher]: 'WAWAW',
    [GameAvatarClass.Paladin]: 'AAWWW',
    [GameAvatarClass.Illusionist]: 'WAAWW',
    [GameAvatarClass.Monk]: 'WAWWA',
    [GameAvatarClass.Scout]: 'WWAWA',
    [GameAvatarClass.Rogue]: 'WWAAW',
    [GameAvatarClass.Strategist]: 'WWWAA',
    [GameAvatarClass.Mentalist]: 'WMWWW',
    [GameAvatarClass.Juggernaut]: 'MWWWW',
    [GameAvatarClass.Hunter]: 'WWMWW',
    [GameAvatarClass.Scientist]: 'WWWWM',
    [GameAvatarClass.Guard]: 'WWWMW',
};
const TIER_BY_CODE = {
    M: StatTier.Main, A: StatTier.Advanced, U: StatTier.Universal, W: StatTier.Weak,
};
// Ability order used by CLASS_TIER_CODES — the GAME ordinal order.
const GAME_ABILITY_ORDER = [
    GameAbilityType.Strength,
    GameAbilityType.PerceptionOrPsiEnergy,
    GameAbilityType.Agility,
    GameAbilityType.Defense,
    GameAbilityType.Intellect,
];
/** The stat tier a class assigns to one ability. */
export function statTierFor(avatarClass, ability) {
    const code = CLASS_TIER_CODES[avatarClass];
    return TIER_BY_CODE[code[GAME_ABILITY_ORDER.indexOf(ability)]];
}
/** Editor AvatarAbility → game GameAbilityType (naming skew: Perception→
 *  PerceptionOrPsiEnergy, Defence→Defense). */
export const ABILITY_EDITOR_TO_GAME = {
    [AvatarAbility.Strength]: GameAbilityType.Strength,
    [AvatarAbility.Agility]: GameAbilityType.Agility,
    [AvatarAbility.Perception]: GameAbilityType.PerceptionOrPsiEnergy,
    [AvatarAbility.Intellect]: GameAbilityType.Intellect,
    [AvatarAbility.Defence]: GameAbilityType.Defense,
};
// ── Ability & power derivation ──────────────────────────────────────────────
/** One ability value `x` (AbilityCalculator.GetStength/…): the number fed into the
 *  success curve. `stat = (int)(statMult * rarityMult * levelMult)`. */
export function deriveAbility(avatarClass, rarity, level, ability) {
    const tier = statTierFor(avatarClass, ability);
    return Math.trunc(STAT_MULTIPLIER[tier] * RARITY_MULTIPLIER[rarity] * levelMultiplier(level));
}
/** All five ability values for an avatar, keyed by GameAbilityType. */
export function deriveAbilities(avatarClass, rarity, level) {
    const out = {};
    for (const ability of GAME_ABILITY_ORDER) {
        out[ability] = deriveAbility(avatarClass, rarity, level, ability);
    }
    return out;
}
/** Starting mission energy budget (AbilityCalculator.GetPower:217):
 *  `(int)(statMult(Universal) * rarityMult * levelMult * 5)`. */
export function derivePower(rarity, level) {
    return Math.trunc(STAT_MULTIPLIER[StatTier.Universal] * RARITY_MULTIPLIER[rarity] * levelMultiplier(level) * 5);
}
/** The game's max level per rarity (AvatarStorage.CreateLevels — number of level
 *  rows per Rarity). Higher rarity tops out lower: Common 50 … Mythic 10. */
export const MAX_LEVEL_BY_RARITY = {
    [AvatarRarity.Common]: 50,
    [AvatarRarity.Rare]: 40,
    [AvatarRarity.Epic]: 30,
    [AvatarRarity.Legendary]: 20,
    [AvatarRarity.Mythic]: 10,
};
/** Max level an avatar of this rarity can reach (falls back to 30). */
export const maxLevelForRarity = (rarity) => { var _a; return (_a = MAX_LEVEL_BY_RARITY[rarity]) !== null && _a !== void 0 ? _a : 30; };
/** GetXMid — AdventureService.cs:842. */
export function getXMid(xLow, difficulty) {
    return (xLow + difficulty) / 2;
}
/** GetK — AdventureService.cs:861. Note the divisor `(difficulty - xLow)`: the
 *  game requires difficulty > xLow, else the curve inverts/degenerates. */
export function getK(pLow, pHigh, xLow, difficulty) {
    return (2 * Math.log((1 / pLow - 1) / (1 / pHigh - 1))) / (difficulty - xLow);
}
/** Success chance for one attempt (AdventureService.GetChallengeSuccessChanceFactor):
 *  `p = 0.9 / (1 + e^(-k(x - xMid)))`, capped at the 0.9 ceiling, floored to 0.05
 *  when it would fall below 0.01 (AdventureService.cs:807-813).
 *  @param x the avatar's value for the chosen approach ability. */
export function successChanceFactor(x, difficulty, tuning) {
    const xMid = getXMid(tuning.xLow, difficulty);
    const k = getK(tuning.pLow, tuning.pHigh, tuning.xLow, difficulty);
    let p = 0.9 / (1 + Math.exp(-k * (x - xMid)));
    if (p < 0.01)
        return { p: 0.05, clamped: true };
    return { p, clamped: false };
}
/** Success reward multiplier band (AdventureService.cs:282-311). `roll` ∈ [0,1). */
export function successDegree(roll) {
    if (roll < 0.5)
        return { type: 'SuccessX1', multiplier: 1 };
    if (roll < 0.72)
        return { type: 'SuccessX1_5', multiplier: 1.5 };
    if (roll < 0.85)
        return { type: 'SuccessX2', multiplier: 2 };
    if (roll < 0.93)
        return { type: 'SuccessX3', multiplier: 3 };
    if (roll < 0.98)
        return { type: 'SuccessX4', multiplier: 4 };
    return { type: 'SuccessX5', multiplier: 5 };
}
/** Failure energy-penalty band (AdventureService.cs:328-337). `roll` ∈ [0,1). */
export function failureDegree(roll) {
    return roll < 0.8 ? { type: 'FailureX1', multiplier: 1 } : { type: 'FailureX2', multiplier: 2 };
}
// ── Resource transmit drop chance (SkillCalculator.cs) ───────────────────────
// The per-attempt probability that an equipped transmitter moves one unit of its
// resource out of a scanned cell, driven by the transmitter's research level.
// Pinned to the C#: BaseDropChance 0.10, +0.005 per level (L1→10%, L11→15%, L21→20%).
export const BASE_DROP_CHANCE = 0.10;
export const LEVEL_DROP_CHANCE_INCREASE = 0.005;
function roundTo(value, decimals) {
    const f = 10 ** decimals;
    return Math.round(value * f) / f;
}
/** Roll target for one transmit attempt at the given research level, in [0,1]. */
export function dropChanceFactor(level) {
    return roundTo(BASE_DROP_CHANCE + (Math.max(1, level) - 1) * LEVEL_DROP_CHANCE_INCREASE, 3);
}
/** The % shown on a transmitter/resource card (factor × 100). */
export function dropChancePercent(level) {
    return roundTo(dropChanceFactor(level) * 100, 2);
}
/** The seeded portal tuning the game ships with (PortalStorage.cs) — the default
 *  when a level authors none. */
export const DEFAULT_PORTAL_TUNING = { pLow: 0.4, pHigh: 0.8, xLow: 40, moveEnergyCost: 30 };
/** Derive a snapshot from a catalog choice (class + rarity + level). */
export function buildAvatarSnapshot(avatarClass, rarity, level) {
    return {
        avatarClass,
        rarity,
        level,
        abilities: deriveAbilities(avatarClass, rarity, level),
        startingEnergy: derivePower(rarity, level),
    };
}
/**
 * Adds selected equipment (script and/or shield) onto an avatar snapshot. The gear's
 * characteristics add FLAT — power is a plain sum (agent.power + script.power +
 * shield.power), exactly as the game stacks equipment: each item's `power` is the
 * sum of its own five characteristics (powerBonus), so equipping simply adds that
 * sum to the avatar's power and each stat bonus to the matching ability. No rarity/
 * level scaling — that inflated the total wildly (a Mythic Lv10 turned ~1.4k into
 * ~18.5k, which read as multiplication).
 */
export function applyEquipmentBonuses(snapshot, items) {
    const abilities = Object.assign({}, snapshot.abilities);
    let startingEnergy = snapshot.startingEnergy;
    for (const it of items) {
        abilities[GameAbilityType.Strength] += it.strength;
        abilities[GameAbilityType.PerceptionOrPsiEnergy] += it.perception;
        abilities[GameAbilityType.Agility] += it.agility;
        abilities[GameAbilityType.Defense] += it.defence;
        abilities[GameAbilityType.Intellect] += it.intellect;
        startingEnergy += it.power;
    }
    return Object.assign(Object.assign({}, snapshot), { abilities, startingEnergy });
}
/** Project a catalog script/shield into the additive bonus shape. */
export function toEquipmentBonus(item) {
    return {
        strength: item.strengthBonus,
        perception: item.perceptionBonus,
        agility: item.agilityBonus,
        defence: item.defenceBonus,
        intellect: item.intellectBonus,
        power: item.powerBonus,
    };
}
/** The avatar's value for one editor-authored ability (bridges the naming skew). */
export function abilityValueFor(snapshot, editorAbility) {
    return snapshot.abilities[ABILITY_EDITOR_TO_GAME[editorAbility]];
}
